/**
 * Charter party register ve formu metinleri (aşama V4; ADR 0084; ortağın `sefer-4.jpeg`,
 * `sefer-5.jpeg`). Ekran adları İngilizce; Türkçe ikinci dil.
 */

export const charterPartiesEn = {
  title: 'Charter Party Register',
  description:
    'Charter parties of the vessel: terms entered once, the charter period from the On Hire to the Off Hire report, and passages outside any charter.',
  vessel: 'Vessel',
  noVessel: 'Select a vessel',
  new: 'New Charter Party',
  shipNote:
    'Charter parties are kept by the office. On board this register is read-only; the charter period follows the On Hire and Off Hire reports entered here.',
  voyageNote:
    'A voyage does not start the charter: the charter period runs from the On Hire to the Off Hire report.',
  list: 'Charter Parties',
  empty: 'No charter party recorded for this vessel.',
  emptyOffice:
    'Record the charter party once; the analysis takes the period from the vessel reports.',
  error: 'The register could not be loaded.',
  retry: 'Try again',
  columns: {
    cpNo: 'CP No',
    charterer: 'Charterer',
    type: 'Type',
    delivery: 'Delivery',
    redelivery: 'Redelivery',
    status: 'Status',
    source: 'Source',
    actions: 'Actions',
  },
  types: { time: 'Time charter', voyage: 'Voyage charter' },
  status: { planned: 'Planned', on_hire: 'On Hire', redelivered: 'Redelivered' },
  basis: {
    report: 'On Hire / Off Hire report',
    voyage: 'Voyage {{no}}',
    planned: 'Planned',
  },
  open: 'Open',
  sourceLabel: { office: 'Office', migrated: 'From voyage {{no}}' },
  overlap: 'Overlaps another charter',
  overlapHint: 'The period overlaps {{list}}. Each charter is analysed with its own period.',
  performance: 'Performance',
  edit: 'Edit',
  editTitle: 'Edit {{no}}',
  remove: 'Remove',
  removeTitle: 'Remove {{no}}',
  removeConfirm:
    'Remove this charter party from the register? Its decisions and review trail are kept.',
  removed: 'Charter party removed.',
  saved: 'Charter party saved.',
  cancel: 'Cancel',
  save: 'Save',
  passages: {
    title: 'Passages Outside Charter Periods',
    description:
      'Sea passages (COSP → EOSP) not covered by any charter. They are not charged to a charterer; the office classifies them. The suggestion comes from the Departure employment.',
    empty: 'Every passage is within a charter period.',
    start: 'COSP (UTC)',
    end: 'EOSP (UTC)',
    hours: 'Hours',
    suggested: 'Suggested',
    classification: 'Class',
    decision: 'Decision',
    ongoing: 'At sea',
    classify: 'Classify',
    classifyTitle: 'Classify passage {{at}}',
    reason: 'Reason',
    reasonHint: 'Kept in the charter party trail; it cannot be changed later.',
    reference: 'Reference (optional)',
    referenceHint: 'e.g. owner instruction or fixture message',
    classified: 'Passage classified.',
    by: '{{name}} · {{at}} UTC',
    classes: {
      owners_account: "Owner's Account",
      awaiting_employment: 'Awaiting Employment',
      unclassified: 'Unclassified',
    },
  },
  form: {
    newTitle: 'New Charter Party',
    contract: 'Contract',
    charterer: 'Charterer',
    cpNo: 'CP No',
    cpDate: 'CP Date',
    cpType: 'Charter Type',
    delivery: 'Delivery & Redelivery',
    deliveryNote:
      'Link the On Hire and Off Hire reports entered on board; until then the planned dates are shown as planned.',
    deliveryPlace: 'Delivery Place',
    deliveryAt: 'Planned Delivery (UTC)',
    deliveryReport: 'On Hire Report',
    redeliveryPlace: 'Redelivery Place',
    redeliveryAt: 'Planned Redelivery (UTC)',
    redeliveryReport: 'Off Hire Report',
    noReport: 'Not reported yet',
    speed: 'Speed & Consumption',
    laden: 'Laden',
    ballast: 'Ballast',
    speedLaden: 'Speed Laden (kn)',
    speedBallast: 'Speed Ballast (kn)',
    consumptionLaden: 'Consumption Laden (MT/day)',
    consumptionBallast: 'Consumption Ballast (MT/day)',
    speedTolerance: '"About" Speed (kn)',
    consumptionTolerance: '"About" Consumption (%)',
    mainFuel: 'Main Engine Fuel',
    auxFuel: 'Aux. Engine Fuel',
    weather: 'Weather Limits',
    maxBeaufort: 'Beaufort ≤',
    maxDouglas: 'Douglas Sea State ≤',
    maxWaveHeight: 'Significant Wave Hs ≤ (m)',
    maxSwell: 'Swell ≤ (m)',
    minDayHours: 'Shortest Day Counted (h)',
    currentClause: 'Current Clause',
    exclusions: 'Exclusions',
    exclusionsHint:
      'Recorded as in the charter party. Felix always excludes straits, canals, anchorage, drifting and waiting from performance time.',
    exclusion: {
      canal: 'Canal',
      strait: 'Strait',
      anchorage: 'Anchorage',
      drift: 'Drifting',
      waiting: 'Waiting',
      port: 'Port',
      manual: 'Manual (office)',
    },
    notes: 'Notes',
  },
  errors: {
    REQUIRED: 'Required.',
    OUT_OF_RANGE: 'Out of range.',
    NOT_INTEGER: 'Whole number.',
    TOO_LONG: 'Too long.',
    INVALID_DATE: 'Invalid date.',
    INVALID_DATETIME: 'Invalid date and time.',
    DUPLICATE: 'Listed twice.',
    SAME_REPORT: 'On Hire and Off Hire must be different reports.',
    REDELIVERY_BEFORE_DELIVERY: 'Redelivery cannot be before delivery.',
    CP_REPORT_KIND: 'Select an On Hire report for delivery and an Off Hire report for redelivery.',
    INVALID_CHARTER_PARTY: 'Check the highlighted fields.',
    INVALID_CP_EVENT: 'A reason is required.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reload and try again.',
    ANALYSIS_CHANGED: 'The analysis changed since you opened it. Reload and review again.',
    FORBIDDEN: 'You are not allowed to do this.',
    NOT_FOUND: 'Not found; it may have been removed.',
    OFFICE_IS_WRITER: 'Charter parties are recorded in the office.',
    GENERIC: 'The request failed. Try again.',
  },
};

export const charterPartiesTr: typeof charterPartiesEn = {
  title: 'Çarter Sözleşmeleri',
  description:
    'Geminin çarter sözleşmeleri: şartlar bir kez girilir, çarter dönemi On Hire raporundan Off Hire raporuna kadardır; hiçbir çartere girmeyen seyirler ayrıca listelenir.',
  vessel: 'Gemi',
  noVessel: 'Gemi seçin',
  new: 'Yeni Çarter Sözleşmesi',
  shipNote:
    'Çarter sözleşmelerini ofis tutar. Gemide bu liste salt okunurdur; çarter dönemi burada girilen On Hire ve Off Hire raporlarını izler.',
  voyageNote:
    'Sefer çarteri başlatmaz: çarter dönemi On Hire raporundan Off Hire raporuna kadardır.',
  list: 'Çarter Sözleşmeleri',
  empty: 'Bu gemi için çarter sözleşmesi yok.',
  emptyOffice: 'Sözleşmeyi bir kez girin; analiz dönemi gemi raporlarından alır.',
  error: 'Liste yüklenemedi.',
  retry: 'Yeniden dene',
  columns: {
    cpNo: 'CP No',
    charterer: 'Kiracı',
    type: 'Tür',
    delivery: 'Teslim',
    redelivery: 'İade',
    status: 'Durum',
    source: 'Kaynak',
    actions: 'İşlemler',
  },
  types: { time: 'Zaman çarteri', voyage: 'Sefer çarteri' },
  status: { planned: 'Planlandı', on_hire: 'Kirada', redelivered: 'İade edildi' },
  basis: {
    report: 'On Hire / Off Hire raporu',
    voyage: 'Sefer {{no}}',
    planned: 'Planlanan',
  },
  open: 'Açık',
  sourceLabel: { office: 'Ofis', migrated: 'Sefer {{no}} kaydından' },
  overlap: 'Başka çarterle örtüşüyor',
  overlapHint: 'Dönem {{list}} ile örtüşüyor. Her çarter kendi dönemiyle analiz edilir.',
  performance: 'Performans',
  edit: 'Düzenle',
  editTitle: '{{no}} düzenle',
  remove: 'Kaldır',
  removeTitle: '{{no}} kaldır',
  removeConfirm: 'Bu çarter sözleşmesi listeden kaldırılsın mı? Kararları ve inceleme izi korunur.',
  removed: 'Çarter sözleşmesi kaldırıldı.',
  saved: 'Çarter sözleşmesi kaydedildi.',
  cancel: 'Vazgeç',
  save: 'Kaydet',
  passages: {
    title: 'Çarter Dönemi Dışındaki Seyirler',
    description:
      'Hiçbir çarter dönemine girmeyen seyirler (COSP → EOSP). Kiracıya yazılmaz; ofis sınıflandırır. Öneri Departure raporundaki istihdam bilgisinden gelir.',
    empty: 'Bütün seyirler bir çarter dönemi içinde.',
    start: 'COSP (UTC)',
    end: 'EOSP (UTC)',
    hours: 'Saat',
    suggested: 'Öneri',
    classification: 'Sınıf',
    decision: 'Karar',
    ongoing: 'Denizde',
    classify: 'Sınıflandır',
    classifyTitle: '{{at}} seyrini sınıflandır',
    reason: 'Gerekçe',
    reasonHint: 'Çarter izinde saklanır; sonradan değiştirilemez.',
    reference: 'Kaynak (isteğe bağlı)',
    referenceHint: 'ör. armatör talimatı ya da fixture mesajı',
    classified: 'Seyir sınıflandırıldı.',
    by: '{{name}} · {{at}} UTC',
    classes: {
      owners_account: 'Armatör Hesabı',
      awaiting_employment: 'İş Bekliyor',
      unclassified: 'Sınıflandırılmadı',
    },
  },
  form: {
    newTitle: 'Yeni Çarter Sözleşmesi',
    contract: 'Sözleşme',
    charterer: 'Kiracı',
    cpNo: 'CP No',
    cpDate: 'CP Tarihi',
    cpType: 'Çarter Türü',
    delivery: 'Teslim ve İade',
    deliveryNote:
      'Gemide girilen On Hire ve Off Hire raporlarını bağlayın; o zamana kadar planlanan tarihler "planlanan" diye gösterilir.',
    deliveryPlace: 'Teslim Yeri',
    deliveryAt: 'Planlanan Teslim (UTC)',
    deliveryReport: 'On Hire Raporu',
    redeliveryPlace: 'İade Yeri',
    redeliveryAt: 'Planlanan İade (UTC)',
    redeliveryReport: 'Off Hire Raporu',
    noReport: 'Henüz raporlanmadı',
    speed: 'Hız ve Tüketim',
    laden: 'Yüklü',
    ballast: 'Balast',
    speedLaden: 'Hız Yüklü (kn)',
    speedBallast: 'Hız Balast (kn)',
    consumptionLaden: 'Tüketim Yüklü (MT/gün)',
    consumptionBallast: 'Tüketim Balast (MT/gün)',
    speedTolerance: '"About" Hız (kn)',
    consumptionTolerance: '"About" Tüketim (%)',
    mainFuel: 'Ana Makine Yakıtı',
    auxFuel: 'Yardımcı Makine Yakıtı',
    weather: 'Hava Sınırları',
    maxBeaufort: 'Beaufort ≤',
    maxDouglas: 'Douglas Deniz Durumu ≤',
    maxWaveHeight: 'Belirgin Dalga Hs ≤ (m)',
    maxSwell: 'Swell ≤ (m)',
    minDayHours: 'Sayılan En Kısa Gün (saat)',
    currentClause: 'Akıntı Şartı',
    exclusions: 'Hariç Tutulanlar',
    exclusionsHint:
      'Sözleşmedeki gibi kaydedilir. Felix boğaz, kanal, demir, sürüklenme ve beklemeyi performans süresinden her zaman düşer.',
    exclusion: {
      canal: 'Kanal',
      strait: 'Boğaz',
      anchorage: 'Demir',
      drift: 'Sürüklenme',
      waiting: 'Bekleme',
      port: 'Liman',
      manual: 'Elle (ofis)',
    },
    notes: 'Notlar',
  },
  errors: {
    REQUIRED: 'Zorunlu.',
    OUT_OF_RANGE: 'Aralık dışında.',
    NOT_INTEGER: 'Tam sayı olmalı.',
    TOO_LONG: 'Çok uzun.',
    INVALID_DATE: 'Geçersiz tarih.',
    INVALID_DATETIME: 'Geçersiz tarih ve saat.',
    DUPLICATE: 'İki kez seçilmiş.',
    SAME_REPORT: 'On Hire ve Off Hire ayrı raporlar olmalı.',
    REDELIVERY_BEFORE_DELIVERY: 'İade teslimden önce olamaz.',
    CP_REPORT_KIND: 'Teslim için On Hire, iade için Off Hire raporu seçin.',
    INVALID_CHARTER_PARTY: 'İşaretli alanları kontrol edin.',
    INVALID_CP_EVENT: 'Gerekçe zorunlu.',
    ROW_VERSION_CONFLICT: 'Kayıt başka biri tarafından değiştirildi. Yenileyip tekrar deneyin.',
    ANALYSIS_CHANGED: 'Siz açtıktan sonra analiz değişti. Yenileyip yeniden inceleyin.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Bulunamadı; kaldırılmış olabilir.',
    OFFICE_IS_WRITER: 'Çarter sözleşmeleri ofiste girilir.',
    GENERIC: 'İstek başarısız oldu. Tekrar deneyin.',
  },
};

/** Charter Party Performance analizi (aşama V5; ADR 0084 Ek). */
export const cpAnalysisEn = {
  description:
    'Actual against contractual performance of one charter party: speed, consumption and sea time on good weather days, excluded periods and the leg analysis within the charter period.',
  charterParty: 'Charter Party',
  noCpTitle: 'No charter party',
  noCp: 'Record the charter party in the Charter Party Register; the analysis takes its period from the On Hire and Off Hire reports.',
  openRegister: 'Open Charter Party Register',
  error: 'The analysis could not be loaded.',
  label: {
    indicative: 'Indicative — ship-observed weather',
    reviewed: 'Reviewed by office',
  },
  reviewedBy: 'Reviewed by {{name}} · {{at}} UTC',
  outdated:
    'Reports or decisions changed after the review on {{at}} UTC; the result is indicative again until the office reviews it.',
  claims: 'Performance claims require independent weather data per the charter party.',
  period: 'Charter Period',
  periodValue: '{{from}} → {{to}}',
  plannedBasis: 'Period from planned dates: no On Hire report linked yet.',
  terms: 'Charter Party Terms',
  speed: 'CP Speed (Laden / Ballast)',
  consumption: 'CP Consumption (Laden / Ballast)',
  weather: 'Weather Limits',
  douglasLimit: 'Douglas ≤ {{value}}',
  swellLimit: 'Swell ≤ {{value}} m',
  about: '"About" Tolerances',
  aboutValue: 'speed {{speed}} kn · consumption {{consumption}} %',
  current: 'Current Clause',
  exclusions: 'Exclusions',
  minDay: 'Shortest Day Counted',
  comparison: 'Actual vs Contractual',
  seaTime: 'Sea Time',
  seaTimeTable: 'Sea time compared with the charter party',
  contractualHours: 'Contractual',
  goodWeatherShort: 'Good weather',
  wholeShort: 'Whole period',
  seaTimeNote: 'Contractual time = distance / CP speed. Difference: + time lost, − time gained.',
  legPeriod: 'COSP → EOSP (UTC)',
  relationHead: 'Period',

  actualHours: 'Actual',
  timeLost: 'Time lost',
  timeGained: 'Time gained',
  allowance: 'With "about" tolerance: speed ≥ {{speed}} kn, consumption ≤ {{consumption}} MT/day',
  allowanceVerdict: { true: 'within', false: 'outside', null: 'not evaluated' },
  strictNote:
    'The CP status applies no tolerance (strictest reading); the "about" line is for reference.',
  excluded: 'Sea Time & Excluded Periods',
  excludedTable: 'Excluded periods',
  noExcluded: 'No excluded periods.',
  kind: 'Type',
  from: 'From (UTC)',
  to: 'To (UTC)',
  hours: 'Hours',
  reason: 'Reason',
  kinds: {
    strait: 'Strait',
    canal: 'Canal',
    anchorage: 'Anchorage',
    drift: 'Drifting',
    waiting: 'Waiting',
    partial_leg: 'Leg partly outside the period',
    office_leg: 'Leg excluded by office',
    office_day: 'Day excluded by office',
  },
  legs: 'Leg Analysis',
  legsTable: 'Sea passages in the charter period',
  noLegs: 'No sea passage (COSP → EOSP) in the charter period yet.',
  relation: { inside: 'Within period', partial: 'Partly outside' },
  counted: 'Counted',
  included: 'Included',
  excludedBadge: 'Excluded',
  byOffice: 'by office',
  exclude: 'Exclude',
  include: 'Include',
  excludeLeg: 'Exclude leg {{at}}',
  includeLeg: 'Include leg {{at}}',
  excludeDay: 'Exclude day {{at}}',
  includeDay: 'Include day {{at}}',
  decisionReason: 'Reason',
  decisionHint: 'Kept in the charter party trail; it cannot be changed later.',
  decisionReference: 'Reference (optional)',
  decisionReferenceHint: 'e.g. charterer weather report WR-2026-10-12',
  decided: 'Decision recorded; the analysis is recalculated.',
  dayExclusion: {
    segment: 'Strait / canal / anchorage',
    office: 'Excluded by office',
    short: 'Shorter than the CP day',
  },
  officeIncluded: 'Included by office',
  review: 'Office Review',
  reviewTrail: 'Decisions and reviews',
  noTrail: 'No office decision yet.',
  markReviewed: 'Mark as Reviewed',
  reviewNote: 'Review note (optional)',
  reviewed: 'Analysis marked as reviewed.',
  shipNote: 'Decisions and the review are made in the office; on board the analysis is read-only.',
  actions: {
    exclude: 'Excluded',
    include: 'Included',
    review: 'Reviewed',
    classify: 'Classified',
  },
  targets: { day: 'Day {{at}}', leg: 'Leg from {{at}}', passage: 'Passage from {{at}}' },
  trailBy: '{{name}} · {{at}} UTC',
  cancel: 'Cancel',
};

export const cpAnalysisTr: typeof cpAnalysisEn = {
  description:
    'Bir çarter sözleşmesinin gerçekleşen ve sözleşmedeki performansı: iyi hava günlerinde hız, tüketim ve seyir süresi, hariç dönemler ve çarter dönemi içindeki bacak analizi.',
  charterParty: 'Çarter Sözleşmesi',
  noCpTitle: 'Çarter sözleşmesi yok',
  noCp: 'Sözleşmeyi Çarter Sözleşmeleri listesine girin; analiz dönemi On Hire ve Off Hire raporlarından alır.',
  openRegister: 'Çarter Sözleşmelerini Aç',
  error: 'Analiz yüklenemedi.',
  label: {
    indicative: 'Gösterge niteliğinde — gemi gözlemi hava',
    reviewed: 'Ofis inceledi',
  },
  reviewedBy: '{{name}} inceledi · {{at}} UTC',
  outdated:
    '{{at}} UTC incelemesinden sonra raporlar ya da kararlar değişti; ofis yeniden inceleyene kadar sonuç gösterge niteliğindedir.',
  claims: 'Performans iddiası, sözleşmeye göre bağımsız hava verisi ister.',
  period: 'Çarter Dönemi',
  periodValue: '{{from}} → {{to}}',
  plannedBasis: 'Dönem planlanan tarihlerden: henüz On Hire raporu bağlanmadı.',
  terms: 'Çarter Şartları',
  speed: 'CP Hızı (Yüklü / Balast)',
  consumption: 'CP Tüketimi (Yüklü / Balast)',
  weather: 'Hava Sınırları',
  douglasLimit: 'Douglas ≤ {{value}}',
  swellLimit: 'Swell ≤ {{value}} m',
  about: '"About" Toleransları',
  aboutValue: 'hız {{speed}} kn · tüketim %{{consumption}}',
  current: 'Akıntı Şartı',
  exclusions: 'Hariç Tutulanlar',
  minDay: 'Sayılan En Kısa Gün',
  comparison: 'Gerçekleşen ve Sözleşme',
  seaTime: 'Seyir Süresi',
  seaTimeTable: 'Seyir süresinin sözleşmeyle karşılaştırması',
  contractualHours: 'Sözleşme',
  goodWeatherShort: 'İyi hava',
  wholeShort: 'Bütün dönem',
  seaTimeNote: 'Sözleşme süresi = mesafe / CP hızı. Fark: + kaybedilen, − kazanılan zaman.',
  legPeriod: 'COSP → EOSP (UTC)',
  relationHead: 'Dönem',

  actualHours: 'Gerçekleşen',
  timeLost: 'Kaybedilen zaman',
  timeGained: 'Kazanılan zaman',
  allowance: '"About" toleransıyla: hız ≥ {{speed}} kn, tüketim ≤ {{consumption}} MT/gün',
  allowanceVerdict: { true: 'içinde', false: 'dışında', null: 'değerlendirilmedi' },
  strictNote: 'CP durumu tolerans uygulamaz (en sıkı okuma); "about" satırı bilgi içindir.',
  excluded: 'Seyir Süresi ve Hariç Dönemler',
  excludedTable: 'Hariç dönemler',
  noExcluded: 'Hariç dönem yok.',
  kind: 'Tür',
  from: 'Başlangıç (UTC)',
  to: 'Bitiş (UTC)',
  hours: 'Saat',
  reason: 'Gerekçe',
  kinds: {
    strait: 'Boğaz',
    canal: 'Kanal',
    anchorage: 'Demir',
    drift: 'Sürüklenme',
    waiting: 'Bekleme',
    partial_leg: 'Kısmen dönem dışındaki bacak',
    office_leg: 'Ofisin hariç tuttuğu bacak',
    office_day: 'Ofisin hariç tuttuğu gün',
  },
  legs: 'Bacak Analizi',
  legsTable: 'Çarter dönemindeki seyirler',
  noLegs: 'Çarter döneminde henüz seyir (COSP → EOSP) yok.',
  relation: { inside: 'Dönem içinde', partial: 'Kısmen dışında' },
  counted: 'Sayılıyor',
  included: 'Dahil',
  excludedBadge: 'Hariç',
  byOffice: 'ofis kararı',
  exclude: 'Hariç tut',
  include: 'Dahil et',
  excludeLeg: '{{at}} bacağını hariç tut',
  includeLeg: '{{at}} bacağını dahil et',
  excludeDay: '{{at}} gününü hariç tut',
  includeDay: '{{at}} gününü dahil et',
  decisionReason: 'Gerekçe',
  decisionHint: 'Çarter izinde saklanır; sonradan değiştirilemez.',
  decisionReference: 'Kaynak (isteğe bağlı)',
  decisionReferenceHint: 'ör. kiracının hava raporu WR-2026-10-12',
  decided: 'Karar kaydedildi; analiz yeniden hesaplandı.',
  dayExclusion: {
    segment: 'Boğaz / kanal / demir',
    office: 'Ofis hariç tuttu',
    short: 'CP gününden kısa',
  },
  officeIncluded: 'Ofis dahil etti',
  review: 'Ofis İncelemesi',
  reviewTrail: 'Kararlar ve incelemeler',
  noTrail: 'Henüz ofis kararı yok.',
  markReviewed: 'İncelendi Olarak İşaretle',
  reviewNote: 'İnceleme notu (isteğe bağlı)',
  reviewed: 'Analiz incelendi olarak işaretlendi.',
  shipNote: 'Kararlar ve inceleme ofiste verilir; gemide analiz salt okunurdur.',
  actions: {
    exclude: 'Hariç tutuldu',
    include: 'Dahil edildi',
    review: 'İncelendi',
    classify: 'Sınıflandırıldı',
  },
  targets: { day: '{{at}} günü', leg: '{{at}} bacağı', passage: '{{at}} seyri' },
  trailBy: '{{name}} · {{at}} UTC',
  cancel: 'Vazgeç',
};

/**
 * Seferin CP'si ve performans referansı (ADR 0084 Ek 2; sahip kararı 2026-10-20, ortak isteği):
 * Add New Voyage'ın "Charter Party" bölümü, Voyage Details kartı ve analiz ekranlarının etiketi.
 */
export const voyageReferenceEn = {
  title: 'Charter Party & Reference',
  section: 'Charter Party',
  mode: 'Charter party of this voyage',
  modes: {
    none: 'No charter party',
    existing: 'Existing charter party',
    new: 'New charter party',
  },
  select: 'Charter Party (register)',
  selectPlaceholder: 'Select a charter party',
  noRegister: 'No charter party recorded for this vessel yet.',
  suggested: 'Suggested: its charter period covers the planned departure.',
  newHint:
    'The charter party is saved once in the Charter Party Register when you send the voyage; the voyage refers to it. More terms can be edited there.',
  newSaved: 'Charter party {{no}} saved to the register.',
  shipNote:
    'Charter parties are kept by the office. The terms are read-only on board; choose the charter party this voyage runs under.',
  periodNote:
    'A voyage does not start the charter: the charter period runs from the On Hire to the Off Hire report.',
  noCp: "No charter party — owner's reference used",
  noReference:
    "No charter party and no owner's reference: the analysis has no reference speed or consumption.",
  owner: "Owner's Reference",
  ownerVoyage: "Owner's reference for this voyage",
  ownerHint: 'Empty fields use the vessel default (Fleet ▸ Vessels).',
  ownerSource: {
    voyage: 'Set for this voyage',
    vessel: 'Vessel default',
    both: 'This voyage and vessel default',
  },
  vesselDefault: 'Vessel default: {{value}}',
  speedLaden: 'Speed Laden',
  speedBallast: 'Speed Ballast',
  consumptionLaden: 'Consumption Laden',
  consumptionBallast: 'Consumption Ballast',
  speed: 'Speed (Laden / Ballast)',
  consumption: 'Consumption (Laden / Ballast)',
  weather: 'Weather Limits',
  beaufort: 'Beaufort ≤ {{value}}',
  wave: 'Hs ≤ {{value}} m',
  douglas: 'Douglas ≤ {{value}}',
  swell: 'Swell ≤ {{value}} m',
  charterer: 'Charterer',
  cpNo: 'CP No',
  type: 'Type',
  period: 'Charter Period',
  status: 'Status',
  reference: 'Reference',
  basis: {
    linked: 'Planned for this voyage',
    migrated: 'Converted from this voyage',
    period: 'Charter period covers this voyage',
    legacy: 'Recorded on this voyage by an earlier version',
  },
  label: {
    charter_party: 'Charter party {{no}}',
    owner: "Owner's reference",
    none: 'No reference',
  },
  indicative: 'Indicative',
  indicativeNote:
    'Results against this reference are indicative; performance claims require independent weather data.',
  openRegister: 'Open Charter Party Register',
  registerDefault: "Owner's Reference (voyages without a charter party)",
  registerDefaultHint:
    'Vessel default used by the analysis when a voyage has no charter party. The office sets it in Fleet ▸ Vessels; a voyage may override it.',
  fuelSub: 'Ref. {{laden}} / {{ballast}} (L / B) · {{label}} · indicative',
};

export const voyageReferenceTr: typeof voyageReferenceEn = {
  title: 'Çarter Sözleşmesi ve Referans',
  section: 'Çarter Sözleşmesi',
  mode: 'Bu seferin çarter sözleşmesi',
  modes: {
    none: 'Çarter sözleşmesi yok',
    existing: 'Kayıtlı çarter sözleşmesi',
    new: 'Yeni çarter sözleşmesi',
  },
  select: 'Çarter Sözleşmesi (kayıt)',
  selectPlaceholder: 'Çarter sözleşmesi seçin',
  noRegister: 'Bu gemi için henüz çarter sözleşmesi kaydı yok.',
  suggested: 'Öneri: çarter dönemi planlanan kalkışı kapsıyor.',
  newHint:
    'Sefer gönderilirken çarter sözleşmesi Çarter Sözleşmeleri kaydına bir kez yazılır; sefer ona bağlanır. Diğer şartlar orada düzenlenir.',
  newSaved: '{{no}} çarter sözleşmesi kayda yazıldı.',
  shipNote:
    'Çarter sözleşmelerini ofis tutar. Şartlar gemide salt okunurdur; bu seferin hangi sözleşmeyle yapıldığını seçin.',
  periodNote:
    'Sefer çarteri başlatmaz: çarter dönemi On Hire raporundan Off Hire raporuna kadardır.',
  noCp: 'Çarter sözleşmesi yok — armatör referansı kullanılıyor',
  noReference:
    'Çarter sözleşmesi ve armatör referansı yok: analizde referans hız ve tüketim bulunmuyor.',
  owner: 'Armatör Referansı',
  ownerVoyage: 'Bu seferin armatör referansı',
  ownerHint: 'Boş alanlarda geminin varsayılanı kullanılır (Filo ▸ Gemiler).',
  ownerSource: {
    voyage: 'Bu sefer için girildi',
    vessel: 'Gemi varsayılanı',
    both: 'Bu sefer ve gemi varsayılanı',
  },
  vesselDefault: 'Gemi varsayılanı: {{value}}',
  speedLaden: 'Hız (Yüklü)',
  speedBallast: 'Hız (Balastlı)',
  consumptionLaden: 'Tüketim (Yüklü)',
  consumptionBallast: 'Tüketim (Balastlı)',
  speed: 'Hız (Yüklü / Balastlı)',
  consumption: 'Tüketim (Yüklü / Balastlı)',
  weather: 'Hava Sınırları',
  beaufort: 'Beaufort ≤ {{value}}',
  wave: 'Hs ≤ {{value}} m',
  douglas: 'Douglas ≤ {{value}}',
  swell: 'Soluğan ≤ {{value}} m',
  charterer: 'Kiracı',
  cpNo: 'CP No',
  type: 'Tür',
  period: 'Çarter Dönemi',
  status: 'Durum',
  reference: 'Referans',
  basis: {
    linked: 'Bu sefer için planlandı',
    migrated: 'Bu seferden dönüştürüldü',
    period: 'Çarter dönemi bu seferi kapsıyor',
    legacy: 'Önceki sürümde bu sefere kaydedildi',
  },
  label: {
    charter_party: '{{no}} çarter sözleşmesi',
    owner: 'Armatör referansı',
    none: 'Referans yok',
  },
  indicative: 'Gösterge',
  indicativeNote:
    'Bu referansa göre sonuçlar göstergedir; performans talebi bağımsız hava verisi gerektirir.',
  openRegister: 'Çarter Sözleşmelerini Aç',
  registerDefault: 'Armatör Referansı (çarter sözleşmesi olmayan seferler)',
  registerDefaultHint:
    'Seferin çarter sözleşmesi yoksa analizin kullandığı gemi varsayılanı. Ofis Filo ▸ Gemiler ekranında girer; sefer kendi değeriyle değiştirebilir.',
  fuelSub: 'Ref. {{laden}} / {{ballast}} (Y / B) · {{label}} · gösterge',
};
